/**
 * Acceso a comentarios de proyectos/tareas (tabla public.proyecto_comentarios).
 * Toda la autorización ocurre en la base de datos (RLS + trigger): este módulo
 * no decide quién puede ver o escribir; solo envía la intención del usuario.
 */
import { supabase } from "./supabase";

export const HILO_GENERAL = "general";
export const hiloDe = (tareaId) => tareaId || HILO_GENERAL;

const COLUMNAS =
  "id, proyecto_id, tarea_id, tarea_descripcion, autor_user_id, autor_nombre, " +
  "tipo_autor, comentario, es_interno, created_at";

export function nuevoRequestId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  // Respaldo RFC4122 v4 para navegadores sin randomUUID
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
    const r = (crypto.getRandomValues(new Uint8Array(1))[0] & 15);
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

// Traduce errores de PostgREST/Postgres a un mensaje entendible, sin ocultar
// la causa real (se adjunta el mensaje original cuando no es reconocido).
export function mensajeError(error) {
  if (!error) return "";
  const msg = error.message || String(error);
  if (error.code === "42501" || /row-level security|No autorizado|permission denied/i.test(msg))
    return "No tienes permiso para realizar esta acción en este proyecto.";
  if (/Debes iniciar sesión/i.test(msg)) return "Tu sesión expiró. Vuelve a iniciar sesión.";
  if (/Demasiados comentarios/i.test(msg)) return msg;
  if (/vacío|char_length/i.test(msg)) return "El comentario no puede estar vacío.";
  if (/no pertenece/i.test(msg)) return "La tarea no pertenece a este proyecto.";
  if (/Failed to fetch|NetworkError|network/i.test(msg))
    return "No hay conexión con el servidor. Revisa tu internet e intenta de nuevo.";
  return `No se pudo completar la acción: ${msg}`;
}

/**
 * @param {object} p
 * @param {string} p.proyectoId
 * @param {string|null|undefined} p.tareaId  undefined = todo el proyecto,
 *        null = solo comentarios generales, uuid = solo esa tarea.
 */
export async function listarComentarios({ proyectoId, tareaId }) {
  let q = supabase.from("proyecto_comentarios").select(COLUMNAS)
    .eq("proyecto_id", proyectoId)
    .order("created_at", { ascending: true })
    .limit(1000);
  if (tareaId === null) q = q.is("tarea_id", null);
  else if (tareaId) q = q.eq("tarea_id", tareaId);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

export async function enviarComentario({ proyectoId, tareaId, comentario, esInterno = false, requestId }) {
  const texto = (comentario || "").trim();
  if (!texto) throw new Error("El comentario no puede estar vacío");
  const { data, error } = await supabase.from("proyecto_comentarios")
    .insert({
      proyecto_id: proyectoId,
      tarea_id: tareaId || null,
      comentario: texto,
      es_interno: !!esInterno,
      client_request_id: requestId,
    })
    .select(COLUMNAS)
    .single();

  if (error) {
    // Reintento de un envío que ya se guardó (doble clic, red intermitente):
    // la restricción única lo detecta y devolvemos el comentario existente.
    if (error.code === "23505" && /dedupe/.test(error.message || "")) {
      const { data: existente, error: e2 } = await supabase.from("proyecto_comentarios")
        .select(COLUMNAS).eq("client_request_id", requestId).maybeSingle();
      if (!e2 && existente) return existente;
    }
    throw error;
  }
  return data;
}

export async function marcarLectura(proyectoId, hilo = HILO_GENERAL) {
  const { data, error } = await supabase.rpc("marcar_lectura", {
    p_proyecto_id: proyectoId, p_hilo: hilo,
  });
  if (error) throw error;
  return data; // marca anterior (o null)
}

/** Resumen por hilo. Sin proyectoId → todos los proyectos visibles. */
export async function resumenComentarios(proyectoId = null) {
  const { data, error } = await supabase.rpc("comentarios_resumen", { p_proyecto_id: proyectoId });
  if (error) throw error;
  return (data || []).map(r => ({ ...r, total: Number(r.total), nuevos: Number(r.nuevos) }));
}

/** Agrupa filas de comentarios_resumen por proyecto. */
export function agruparResumenPorProyecto(filas) {
  const out = {};
  for (const r of filas) {
    const p = out[r.proyecto_id] ||= { nuevos: 0, total: 0, pendientes: 0, ultimo: null, porTarea: {} };
    p.nuevos += r.nuevos;
    p.total += r.total;
    if (r.pendiente_respuesta) p.pendientes += 1;
    if (!p.ultimo || new Date(r.ultimo_at) > new Date(p.ultimo.ultimo_at)) p.ultimo = r;
    p.porTarea[hiloDe(r.tarea_id)] = r;
  }
  return out;
}

/**
 * Suscripción Realtime a comentarios nuevos de un proyecto (o de todos si
 * proyectoId es null). Realtime aplica el RLS de cada usuario: un cliente no
 * recibe notas internas. Devuelve una función para cancelar.
 */
export function suscribirComentarios(proyectoId, { onInsert, onEstado }) {
  const nombre = `comentarios_${proyectoId || "todos"}_${Math.random().toString(36).slice(2, 8)}`;
  const filtro = { event: "INSERT", schema: "public", table: "proyecto_comentarios" };
  if (proyectoId) filtro.filter = `proyecto_id=eq.${proyectoId}`;
  const canal = supabase.channel(nombre)
    .on("postgres_changes", filtro, payload => onInsert?.(payload.new))
    .subscribe(estado => onEstado?.(estado));
  return () => { supabase.removeChannel(canal); };
}
